import React, { useState, useMemo } from 'react';
import { Table, TableColumn, Pagination } from '@/components/common/table';
import { Button } from '@/components/common/button';
import { Input } from '@/components/common/input';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye,
  Edit,
  Trash2,
  UserCircle,
  Building2
} from 'lucide-react';
import { Client, ClientType } from '@/types/client_types';

interface ClientListProps {
  clients: Client[];
  is_loading?: boolean;
  on_add_client: () => void;
  on_view_client: (client: Client) => void;
  on_edit_client: (client: Client) => void;
  on_delete_client: (client: Client) => void;
  on_export?: () => void;
}

export const ClientList: React.FC<ClientListProps> = ({
  clients,
  is_loading = false,
  on_add_client,
  on_view_client,
  on_edit_client,
  on_delete_client,
  on_export
}) => {
  const [search_term, set_search_term] = useState('');
  const [filter_type, set_filter_type] = useState<ClientType | 'all'>('all');
  const [current_page, set_current_page] = useState(1);
  const [sort_config, set_sort_config] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'name', direction: 'asc' });
  
  const items_per_page = 10;
  
  // Filter and search clients
  const filtered_clients = useMemo(() => {
    let result = [...clients];
    
    // Apply type filter
    if (filter_type !== 'all') {
      result = result.filter(client => client.client_type === filter_type);
    }
    
    // Apply search
    if (search_term) {
      const term = search_term.toLowerCase();
      result = result.filter(client =>
        client.name?.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.phone_number?.toLowerCase().includes(term) ||
        client.id_number?.toLowerCase().includes(term) ||
        client.address?.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      const a_value = a[sort_config.key as keyof Client];
      const b_value = b[sort_config.key as keyof Client];
      
      if (a_value === undefined || b_value === undefined) return 0;
      
      if (a_value < b_value) return sort_config.direction === 'asc' ? -1 : 1;
      if (a_value > b_value) return sort_config.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [clients, filter_type, search_term, sort_config]);
  
  // Pagination
  const total_pages = Math.ceil(filtered_clients.length / items_per_page);
  const paginated_clients = filtered_clients.slice(
    (current_page - 1) * items_per_page,
    current_page * items_per_page
  );
  
  const handle_sort = (key: string) => {
    set_sort_config(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const get_client_type_badge = (type: ClientType) => {
    const styles = {
      TENANT: 'bg-blue-100 text-blue-800',
      LANDLORD: 'bg-purple-100 text-purple-800'
    };
    
    const icons = {
      TENANT: <UserCircle size={14} />,
      LANDLORD: <Building2 size={14} />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[type]}`}>
        {icons[type]}
        {type === 'TENANT' ? 'Tenant' : 'Landlord'}
      </span>
    );
  };
  
  const get_status_badge = (is_active: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
        is_active 
          ? 'bg-success bg-opacity-10 text-success' 
          : 'bg-gray-100 text-gray-600'
      }`}>
        {is_active ? 'Active' : 'Inactive'}
      </span>
    );
  };
  
  const columns: TableColumn<Client>[] = [
    {
      key: 'name',
      header: 'Client Name',
      sortable: true,
      render: (client) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-forest-green text-white flex items-center justify-center font-semibold">
            {client.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-charcoal">{client.name}</p>
            <p className="text-xs text-gray-500">{client.email || client.phone_number}</p>
          </div>
        </div>
      )
    },
    {
      key: 'client_type',
      header: 'Type',
      sortable: true,
      render: (client) => get_client_type_badge(client.client_type)
    },
    {
      key: 'id_number',
      header: 'ID Number',
      render: (client) => (
        <span className="text-sm text-gray-700">{client.id_number || '-'}</span>
      )
    },
    {
      key: 'phone_number',
      header: 'Contact',
      render: (client) => (
        <div className="text-sm">
          <p className="text-gray-700">{client.phone_number || '-'}</p>
          {client.email && (
            <p className="text-gray-500 text-xs">{client.email}</p>
          )}
        </div>
      )
    },
    {
      key: 'address',
      header: 'Location',
      render: (client) => (
        <span className="text-sm text-gray-700">{client.address || '-'}</span>
      )
    },
    {
      key: 'meter_count',
      header: 'Meters',
      align: 'center',
      render: (client) => (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-lime-green text-forest-green font-semibold text-sm">
          {(client as any).meter_count || 0}
        </span>
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (client) => get_status_badge(client.is_active)
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (client) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              on_view_client(client);
            }}
            icon_left={<Eye size={16} />}
            className="text-gray-600"
          >
            View
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              on_edit_client(client);
            }}
            icon_left={<Edit size={16} />}
            className="text-forest-green"
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              on_delete_client(client);
            }}
            icon_left={<Trash2 size={16} />}
            className="text-error"
          >
            Delete
          </Button>
        </div>
      )
    }
  ];
  
  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            type="text"
            placeholder="Search by name, email, phone, or ID..."
            value={search_term}
            onChange={(e) => set_search_term(e.target.value)}
            icon_left={<Search size={18} />}
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Filter by Type */}
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-300">
            <Filter size={16} className="text-gray-500" />
            <select
              value={filter_type}
              onChange={(e) => set_filter_type(e.target.value as ClientType | 'all')}
              className="text-sm border-none focus:outline-none cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="TENANT">Tenants</option>
              <option value="LANDLORD">Landlords</option>
            </select>
          </div>
          
          {on_export && (
            <Button
              variant="outline"
              size="md"
              icon_left={<Download size={18} />}
              onClick={on_export}
            >
              Export
            </Button>
          )}
          
          <Button
            variant="primary"
            size="md"
            icon_left={<Plus size={18} />}
            onClick={on_add_client}
          >
            Add Client
          </Button>
        </div>
      </div>
      
      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Total Clients</p>
          <p className="text-2xl font-bold text-charcoal">{filtered_clients.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Tenants</p>
          <p className="text-2xl font-bold text-blue-600">
            {filtered_clients.filter(c => c.client_type === 'TENANT').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Landlords</p>
          <p className="text-2xl font-bold text-purple-600">
            {filtered_clients.filter(c => c.client_type === 'LANDLORD').length}
          </p>
        </div>
      </div>
      
      {/* Table */}
      <Table
        data={paginated_clients}
        columns={columns}
        is_loading={is_loading}
        on_row_click={on_view_client}
        sort_config={sort_config}
        on_sort={handle_sort}
        empty_message="No clients found. Add your first client to get started!"
        striped
        hoverable
      />
      
      {/* Pagination */}
      {total_pages > 1 && (
        <Pagination
          current_page={current_page}
          total_pages={total_pages}
          on_page_change={set_current_page}
          items_per_page={items_per_page}
          total_items={filtered_clients.length}
        />
      )}
    </div>
  );
};